<template>
  <div class="tender-query-edit-mode clearfix">
    <label
      class="emphasis"
      :class="{ 'required-flag': !isBidder }"
    >
      {{ $t('tender.query.closingTime') }}:
    </label>
    <DateTimeSelector
      :date="query.closingDate"
      :timeOptions="closingTimeOptions"
      :disabledDates="disabledDates"
      :required="true"
      :disabled="isBidder"
      :highlightClass="highlightClass"
      :isClosingTime="true"
      @updateDate="val => updateValue('closingDate', val)"
    />

    <question
      :query="query"
      @deleteQuestion="index => deleteQuestion(index)"
      @updateQuestion="(index, fieldName, val) => updateQuestion(index, fieldName, val)"
    />

    <button
      v-if="!isBidder"
      type="btton"
      class="btn add-question-btn"
      @click="addQuestion()"
    >
      {{ $t('action.add') }} {{ $t('tender.query.question') }}
      <i class="icon-plus" />
    </button>

    <div class="query-attachments">
      <div v-if="isBidder && engineerFiles.length">
        <label>{{ $t('tender.tenderAttachment') }}</label>
        <attachments
          :noHighlight="true"
          :editView="false"
          :files.sync="engineerFiles"
          :zipName="`${zipPrefix}_Attachments.zip`"
        />
      </div>
      <label class="emphasis">{{ isBidder ? $t('vendor.attachment.vendor') : $t('attachments') }}: </label>
      <div>
        <FileUpload
          :attachment.sync="attachments"
          :resourceId="getResourceId()"
        />
      </div>
    </div>

    <div class="row action-btns align-right">
      <button
        v-if="withCancelBtn"
        class="btn btn--darkblue"
        @click="cancel"
      >{{ $t('action.cancel') }}</button>
      <button
        class="btn btn--white"
        @click="save()"
      >{{ $t('action.save') }} {{ $t('asDraft') }}</button>
      <button
        v-if="isDraft && isPicView"
        class="btn btn--orange"
        @click="issue()"
      >{{ $t('action.issue') }} {{ $t('tender.query.name') }}</button>
      <DialogSlot
        v-if="isBidder"
        :id="tqSubmitModalId"
        ref="query-submit-modal"
        :modalBtn="$t('action.submit')"
        :showCloseBtn="false"
        :beforeOpenModal="beforeSubmit"
      >
        <div slot="content">
          <span class="modal-pure-text">{{ submitHint }}</span>
        </div>
        <div slot="buttons">
          <button
            v-show="continueSubmit"
            type="button"
            class="btn btn--darkblue"
            data-close
            aria-label="Close reveal"
          >{{ $t('action.cancel') }}</button>
          <button
            type="button"
            class="btn btn--orange"
            @click="next()"
          >{{ continueSubmit ? $t('action.yesSubmit') : $t('action.ok') }}</button>
        </div>
      </DialogSlot>
    </div>
    <DialogSlot
      :id="queryUpdatedWarningId"
      ref="detect-update-modal"
      :showCloseBtn="false"
    >
      <div slot="content">
        <span class="confirm-header">{{ $t('error.updateQuery.410') }}</span>
      </div>
      <div slot="buttons">
        <button
          type="button"
          class="btn btn--darkblue"
          data-close
          @click="refreshPage()"
        >{{ $t('hint.okay') }}</button>
      </div>
    </DialogSlot>
  </div>
</template>

<script>
import Status from 'http-status-codes'
import { mapMutations, mapGetters, mapActions } from 'vuex'

import Attachments from '@/modules/tenders/view/Attachments'
import DateTimeSelector from '@/controls/DateTimeSelector'
import DialogSlot from '@/controls/DialogSlot'
import FileUpload from '@/modules/FileUpload'
import Question from '@/modules/tenders/queries/Question'

import mutationTypes from '@/store/mutation-types'
import navigation from '@/utils/navigation'
import queryMixin from './queryMixin'
import { FORM_SRC_TENDER_QUERY, closingTimeOptions, EmailTypes, TENDER_QUERY_COMPONENT } from '@/utils/consts'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { getAttachments, updateDocuments } from '@/api/docs/request'
import { getRandomIdSuffix } from '@/utils/vueCompHelper'
import { keepQueryEdit } from '@/api/queries/request'
import { KEEP_EDIT_REQUEST_GAP_SECS } from '@/api/tenders/consts'
import { queryStatus } from '@/utils/locale'
import { sendEmail } from '@/api/postman/request'
import { utils as formUtils } from '@/plugin/InputChange'

export default {
  name: TENDER_QUERY_COMPONENT,
  components: {
    DateTimeSelector,
    DialogSlot,
    FileUpload,
    Attachments,
    Question,
  },
  mixins: [queryMixin],

  props: {
    query: {
      type: Object,
      default: null,
    },
    index: {
      type: Number,
      default: 0,
    },
    withCancelBtn: {
      type: Boolean,
      default: false,
    },
    requestIssue: {
      type: Boolean,
      default: false,
    },
  },

  data () {
    return {
      closingTimeOptions,
      invalidClosingDate: false,
      attachments: {
        description: '',
        files: [],
      },
      engineerFiles: [],
      submitHint: '',
      continueSubmit: false,
      tqSubmitModalId: `tq-submit-mdl-${getRandomIdSuffix()}`,
      queryUpdatedWarningId: `query-updated-warn-${getRandomIdSuffix()}`,
      queryCopy: {},
      keepEditTimer: null,
    }
  },

  methods: {
    ...mapMutations('tenders', {
      updateQuery: mutationTypes.TENDER_UPDATE_QUERY_BY_PATH,
    }),
    ...mapActions('tenders', {
      refreshCurrentBid: 'refreshCurrentBid',
      saveTenderQuery: 'saveQuery',
    }),
    refreshPage () {
      location.reload()
    },
    navToDashboard () {
      navigation.toHome()
    },
    getResourceId () {
      return this.isBidder ? this.query.id : this.query.intResourceId
    },

    async next () {
      this.$refs['query-submit-modal'].closeModal()
      if (this.continueSubmit) {
        await this.submit()
        this.continueSubmit = false
      }
      formUtils.resetDirty(FORM_SRC_TENDER_QUERY)
      this.navToDashboard()
    },

    updateValue (fieldName, newValue) {
      formUtils.setDirty(FORM_SRC_TENDER_QUERY)
      this.updateQuery({
        path: [this.index, fieldName],
        newValue,
      })
    },

    deleteQuestion (qIndex) {
      const questions = JSON.parse(JSON.stringify(this.questions))
      questions.splice(qIndex, 1)
      this.refreshQuestionOrder(questions, qIndex)
      this.updateQuery({
        path: [this.index, 'questions'],
        newValue: questions,
      })
    },

    refreshQuestionOrder (questions = [], startIndex) {
      for (let i = startIndex; i < questions.length; i += 1) {
        questions[i].order = i + 1
      }
    },

    addQuestion () {
      const newQuestion = { question: '', answer: '', order: this.questions.length + 1 }
      if (this.questions.length === 0) {
        this.updateQuery({
          path: [this.index, 'questions'],
          newValue: [newQuestion],
        })
        return
      }
      this.updateQuestion(this.questions.length, null, newQuestion)
    },

    updateQuestion (qIndex, fieldName, newValue) {
      let path = [this.index, 'questions', qIndex]
      if (fieldName) {
        path.push(fieldName)
      }
      this.updateQuery({ path, newValue })
    },

    validate () {
      fusionInputCheckUtil.validateAllFields(undefined, '.tender-query-edit-mode')
      const validClosingDate = new Date(this.query.closingDate).getTime() > Date.now()

      if (!validClosingDate) {
        this.continueSubmit = false
        if (this.isBidder) {
          this.submitHint = this.$i18n.t('error.bids.queryOverdue')
          this.$refs['query-submit-modal'].openModal()
        } else {
          this.invalidClosingDate = true
        }
      } else {
        this.invalidClosingDate = false
      }
      return fusionInputCheckUtil.isAllFieldsValid('.tender-query-edit-mode') && validClosingDate
    },
    async cancel () {
      await this.refreshCurrentBid()
      this.disposeTimer()
      return this.$emit('queryCancelEvent')
    },
    async save (showNotification = true) {
      fusionInputCheckUtil.resetAllFields()
      try {
        await this.saveTenderQuery({ query: this.query, showNotification })
      } catch (err) {
        if (err.response && err.response.status === Status.GONE) {
          this.$refs['detect-update-modal'].openModal()
        }
        throw err
      }
      await updateDocuments(this.attachments.files)
      await this.refreshCurrentBid()
      formUtils.resetDirty(FORM_SRC_TENDER_QUERY)
      this.disposeTimer()
      return this.$emit('queryCancelEvent')
    },

    async issue () {
      fusionInputCheckUtil.resetAllFields()
      if (!this.validate()) {
        return false
      }
      await this.updateQueryWithStatus(queryStatus.ISSUED)
      await sendEmail(EmailTypes.tenders.query, this.query)
      formUtils.resetDirty(FORM_SRC_TENDER_QUERY)
      this.disposeTimer()
      return this.$emit('queryCancelEvent')
    },

    beforeSubmit () {
      fusionInputCheckUtil.resetAllFields()
      if (!this.validate()) {
        return false
      }
      this.submitHint = this.$i18n.t('hint.submitQuery')
      this.continueSubmit = true
      return true
    },
    async submit () {
      await this.updateQueryWithStatus(queryStatus.SUBMITTED)
      const templates = [EmailTypes.tenders.submitQuery, EmailTypes.tenders.queryResponse]
      await Promise.all(
        templates.map((template) => sendEmail(template, this.query),
        ),
      )

      this.navToDashboard()
    },
    async keepEdit (queryId) {
      const result = await keepQueryEdit(queryId, this.currentEditAccount)
      if (result && result.keepEdit) {
        this.updateQuery({
          path: [this.index],
          newValue: {
            ...this.currentBid.queries[this.index],
            editAt: result.editAt,
            ...this.currentEditAccount,
          },
        })
        this.keepEditTimer = setTimeout(this.keepEdit, KEEP_EDIT_REQUEST_GAP_SECS * 1000, queryId)
      } else {
        this.cancel()
      }
    },
    disposeTimer () {
      clearTimeout(this.keepEditTimer)
      this.keepEditTimer = null
    },
  },

  async mounted () {
    this.disposeTimer()
    // a cope of initial query, for cancel edit rollback
    this.queryCopy = JSON.parse(JSON.stringify(this.query))
    // check edit button status
    if (this.withCancelBtn) {
      this.keepEdit(this.query.id)
    }
    if (this.isBidder) {
      this.attachments.files = await getAttachments(this.query.id)
      this.engineerFiles = await getAttachments(this.query.intResourceId)
    } else {
      this.attachments.files = await getAttachments(this.query.intResourceId)
    }
    if (this.requestIssue) {
      this.issue()
    }
  },
  beforeDestroy () {
    this.disposeTimer()
  },
  computed: {
    ...mapGetters('tenders', ['isPicView', 'isRepresentative', 'currentBid', 'currentEditAccount']),
    ...mapGetters('user', ['isContractor', 'currentUser']),
    isBidder () {
      return this.isContractor && !this.isRepresentative
    },
    zipPrefix () {
      return `TQ${this.query.order}`
    },
    isDraft () {
      return this.query.status === queryStatus.DRAFT
    },
    disabledDates () {
      const toDate = new Date(Date.now())
      toDate.setDate(toDate.getDate() - 1)
      return {
        to: toDate,
      }
    },

    questions () {
      return this.query.questions || []
    },

    allowDelete () {
      return !this.isBidder && this.questions.length > 1
    },

    highlightClass () {
      return this.invalidClosingDate ? 'error-highlight' : ''
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/tender/query';

.tender-query-edit-mode {
  label {
    margin-top: 0;
  }
}

.add-question-btn {
  width: 100%;
  height: 50px;
  display: block;
  background-color: #FFFFFF;
  border: solid 1px #EBEBEB;
  color: #0C3A58;
  i {
    font-style: normal;
    font-weight: bold;
  }
}

.delete-btn {
  text-align: right;
  display: inline;
  i:after {
    padding-right: 12px;
  }
}

.action-btns {
  margin-top: 20px;

  button {
    width: auto;
    padding: 8px 15px;
    vertical-align: middle;
    cursor: pointer;
  }
  button + button, .dialog-slot {
    margin-left: 24px;
  }
}

.confirm-header {
  font-weight: bold;
}

</style>

<style lang="scss">
.query-attachments {
  .tender-tnc-table .description .file-upload-desc {
    font-size: 14px;
  }
}
</style>
